/**
 * Posting Log Page
 *
 * Reports → Posting Log
 *
 * Permissions gate: postingLog.enabled (mocked — BE to wire in parallel)
 * Mock data is used throughout; real API endpoints will be wired up in parallel
 * by the engineering team.
 *
 * Real endpoints (engineering handoff reference only — not called in this build):
 *   GET  /api/costing-transactions → CostingTransactionLog[]
 *   POST /api/costing-transactions/{logId}/resend → retry a failed line
 */
import React, { useCallback, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/blocks/page-header';
import { ReportNoAccessCard } from '@/components/shared/reports/ReportNoAccessCard';
import { usePermissions } from '@/contexts/PermissionContext';
import { useCompany } from '@/features/multi-company/context/CompanyContext';
import { useReportFilterOptions } from '@/hooks/useReportFilterOptions';
import { useReportColumns } from '@/hooks/useReportColumns';
import { Database, AlertCircle } from 'lucide-react';
import { POSTING_DEFAULT_COLUMNS } from '@/utils/PostingLog/constants';
import { usePostingLogFilters } from '@/hooks/PostingLog/usePostingLogFilters';
import { usePostingLogData } from '@/hooks/PostingLog/usePostingLogData';
import { usePostingLogSelection } from '@/hooks/PostingLog/usePostingLogSelection';
import { usePostingLogRetry } from '@/hooks/PostingLog/usePostingLogRetry';
import { usePostingLogExport } from '@/hooks/PostingLog/usePostingLogExport';
import { SummaryBar } from './SummaryBar';
import { RetryDialog } from './RetryDialog';
import { DrilldownPanel } from './DrilldownPanel';
import { ResultsTable } from './ResultsTable';
import { IntegrationBanner } from './IntegrationBanner';
import { PostingLogToolbar } from './PostingLogToolbar';
import { PostingLogFilterChips } from './PostingLogFilterChips';
import { PostingLogFilterSheet } from './PostingLogFilterSheet';

const PostingLogHeader: React.FC = () => (
  <PageHeader
    title="Posting Log"
    subtitle="ERP posting status for costing transactions"
    size={36}
    showActions={false}
    showBreadcrumb={false}
    icon={<Database />}
  />
);

export const PostingLogPage: React.FC = () => {
  const { canViewPostingLog, canExportPostingLog, canRetryPostingLog } = usePermissions();
  const { currentCompany } = useCompany();

  const canView     = canViewPostingLog();
  const allowExport = canExportPostingLog();
  const allowRetry  = canRetryPostingLog();

  const columns = useReportColumns(POSTING_DEFAULT_COLUMNS);

  // Drill-down panel
  const [drilldownId,   setDrilldownId]   = useState<string | null>(null);
  const [drilldownOpen, setDrilldownOpen] = useState(false);

  const filterOptions = useReportFilterOptions(currentCompany?.id, { enabled: canView, includeCostCentres: true });
  const filters = usePostingLogFilters();
  const { matchesNothing } = filters;
  const {
    result, rows, totalCount, isLoading, loadError, accessDenied, refreshKey, refresh,
    integrationConfigured, retryDisabledReason, disabledRetryIds,
  } = usePostingLogData({ canView, query: filters.query, page: filters.page, matchesNothing, companyId: currentCompany?.id });
  const { selectedIds, handleToggleSelect, handleToggleSelectAll, deselect } = usePostingLogSelection(rows);
  const {
    retryingIds, retryDialogOpen, setRetryDialogOpen, retryTarget,
    handleRetry, handleBulkRetry, handleRetryConfirm,
  } = usePostingLogRetry({ allowRetry, integrationConfigured, selectedIds, deselect, onRetried: refresh });
  const { isExporting, handleExport } = usePostingLogExport({ allowExport, matchesNothing, query: filters.query });

  // ── Drill-down ────────────────────────────────────────────────────────────────
  const handleTimesheetClick = useCallback((timesheetId: string) => {
    setDrilldownId(timesheetId);
    setDrilldownOpen(true);
  }, []);

  // ── Permission gate ───────────────────────────────────────────────────────────
  if (!canView || accessDenied) {
    return (
      <div className="space-y-6">
        <PostingLogHeader />
        <ReportNoAccessCard />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <PostingLogHeader />

      {/* Summary Bar — reflects current filter */}
      <SummaryBar
        total={totalCount}
        posted={matchesNothing ? 0 : result?.postedCount ?? 0}
        failed={matchesNothing ? 0 : result?.failedCount ?? 0}
      />

      {/* Main card */}
      <Card>
        <CardContent className="pt-6 pb-3 px-6">
          <PostingLogToolbar
            filters={filters}
            columns={columns}
            allowExport={allowExport}
            isExporting={isExporting}
            onExport={handleExport}
            allowRetry={allowRetry}
            integrationConfigured={integrationConfigured}
            selectedIds={selectedIds}
            onBulkRetry={handleBulkRetry}
          />

          <PostingLogFilterChips filters={filters} filterOptions={filterOptions} />

          {!integrationConfigured && <IntegrationBanner />}

          {loadError && (
            <div className="flex items-start gap-2 rounded-md border border-error-200 bg-error-50 p-3 text-sm text-error-800 dark:border-error-800 dark:bg-error-900/20 dark:text-error-300 mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
              <span>{loadError}</span>
            </div>
          )}

          {/* Results Table */}
          <ResultsTable
            data={rows}
            totalCount={totalCount}
            isLoading={isLoading}
            onTimesheetClick={handleTimesheetClick}
            onRetry={handleRetry}
            retryingIds={retryingIds}
            disabledRetryIds={disabledRetryIds}
            selectedIds={selectedIds}
            onToggleSelect={handleToggleSelect}
            onToggleSelectAll={handleToggleSelectAll}
            sortKey={filters.sortKey}
            sortDir={filters.sortDir}
            onSort={filters.handleSort}
            page={filters.page}
            onPageChange={filters.setPage}
            allowRetry={allowRetry}
            columnVisibility={columns.columnVisibility}
          />
        </CardContent>
      </Card>

      <PostingLogFilterSheet filters={filters} filterOptions={filterOptions} />

      {/* Retry Confirmation Dialog */}
      <RetryDialog
        open={retryDialogOpen}
        onOpenChange={setRetryDialogOpen}
        count={retryTarget.length}
        onConfirm={handleRetryConfirm}
      />

      {/* Drill-down Panel */}
      <DrilldownPanel
        timesheetId={drilldownId}
        open={drilldownOpen}
        onOpenChange={setDrilldownOpen}
        allowRetry={allowRetry}
        allowExport={allowExport}
        refreshKey={refreshKey}
        retryingIds={retryingIds}
        retryDisabledReason={retryDisabledReason}
        onRetry={handleRetry}
      />
    </div>
  );
};
